import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { FiCheck, FiCopy, FiLayers, FiTrash2, FiX } from "react-icons/fi";

const blockSelector = "[data-preview-question-id], [data-handout-question-uuid], [data-handout-section-id]";
const controlSelector = 'button, a, input, textarea, select, [contenteditable="true"], [data-preview-uuid-ui], [data-preview-no-drag]';
const shortId = (id) => id.length > 19 ? `${id.slice(0, 8)}…${id.slice(-6)}` : id;

// Pointer coordinates already include preview zoom and the current CSS column.
export function previewUuidAnchor(node, x, y) {
  const fragments = Array.from(node.getClientRects()).filter((rect) => rect.width > 0 && rect.height > 0);
  const distance = (rect) => Math.max(rect.left - x, 0, x - rect.right) ** 2 + Math.max(rect.top - y, 0, y - rect.bottom) ** 2;
  const rect = fragments.reduce((nearest, candidate) => !nearest || distance(candidate) < distance(nearest) ? candidate : nearest, null) || node.getBoundingClientRect();
  return { left: Number.isFinite(x) ? x + 14 : rect.left + 14, top: Number.isFinite(y) ? y + 14 : rect.top + 14 };
}

export default function PreviewUuidTools({ children, enabled = true, resetKey = "" }) {
  const host = useRef(null);
  const badge = useRef(null);
  const activeRef = useRef(null);
  const hideTimer = useRef(null);
  const feedbackTimer = useRef(null);
  const requestVersion = useRef(0);
  const copying = useRef(false);
  const anchor = useRef(null);
  const dimensions = useRef({ width: 0, height: 0 });
  const moveFrame = useRef(null);
  const [active, setActive] = useState(null);
  const [ids, setIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);

  const hide = useCallback(() => {
    clearTimeout(hideTimer.current);
    if (moveFrame.current !== null) cancelAnimationFrame(moveFrame.current);
    moveFrame.current = null;
    activeRef.current = null;
    setActive(null);
  }, []);
  const keep = () => clearTimeout(hideTimer.current);
  const leave = () => {
    if (activeRef.current?.pinned) return;
    keep();
    hideTimer.current = setTimeout(hide, 180);
  };

  useEffect(() => {
    requestVersion.current += 1;
    copying.current = false;
    activeRef.current = null;
    setActive(null); setIds([]); setOpen(false); setFeedback(null); setBusy(false);
    return () => {
      requestVersion.current += 1;
      clearTimeout(hideTimer.current); clearTimeout(feedbackTimer.current);
      if (moveFrame.current !== null) cancelAnimationFrame(moveFrame.current);
      moveFrame.current = null;
    };
  }, [resetKey]);

  useEffect(() => {
    if (!enabled) { hide(); setOpen(false); }
  }, [enabled, hide]);

  useEffect(() => {
    if (!enabled) return undefined;
    const dismiss = (event) => {
      if (!host.current?.contains(event.target) && !badge.current?.contains(event.target)) { hide(); setOpen(false); }
    };
    const scroll = (event) => {
      // Scrolling the collected-ID list should not dismiss its feedback.
      if (!event.target?.closest?.("[data-preview-uuid-ui]")) hide();
    };
    const key = (event) => {
      if (event.key === "Escape") { hide(); setOpen(false); }
      else if (["Enter", "PageDown", "PageUp"].includes(event.key) && !event.target?.closest?.("[data-preview-uuid-ui]")) hide();
    };
    document.addEventListener("pointerdown", dismiss, true);
    document.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", hide);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", dismiss, true);
      document.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", hide);
      document.removeEventListener("keydown", key);
    };
  }, [enabled, hide]);

  const placeBadge = useCallback(() => {
    const element = badge.current;
    if (!element || !anchor.current) return;
    element.style.left = `${Math.max(8, Math.min(anchor.current.left, window.innerWidth - dimensions.current.width - 8))}px`;
    element.style.top = `${Math.max(8, Math.min(anchor.current.top, window.innerHeight - dimensions.current.height - 8))}px`;
  }, []);

  useLayoutEffect(() => {
    const element = badge.current;
    if (!element || !active) return;
    const rect = element.getBoundingClientRect();
    dimensions.current = { width: rect.width, height: rect.height };
    placeBadge();
  }, [active, feedback, busy, placeBadge]);

  const reveal = (event, pinned = false) => {
    if (event.target.closest?.(controlSelector)) return;
    const node = event.target.closest?.(blockSelector);
    if (!node || !host.current?.contains(node) || node.closest('[data-reader-measure], [inert], [data-record-page-break]')) {
      if (pinned) hide();
      else leave();
      return;
    }
    keep();
    const questionId = node.dataset.handoutQuestionUuid || node.dataset.previewQuestionId;
    const id = questionId || node.dataset.handoutSectionId;
    if (!id) return;
    const current = activeRef.current;
    if (current?.node === node && (!pinned || current.pinned)) return;
    anchor.current = previewUuidAnchor(node, event.clientX, event.clientY);
    const next = { id, kind: questionId ? "題目 UUID" : "區塊 ID", node, pinned };
    activeRef.current = next;
    setActive(next);
  };

  const follow = (event) => {
    if (event.pointerType === "touch" || event.buttons || event.target.closest?.(controlSelector)) return;
    const current = activeRef.current;
    if (!current || event.target.closest?.(blockSelector) !== current.node) { reveal(event); return; }
    if (current.pinned) activeRef.current = { ...current, pinned: false };
    // Pause in a small halo so the user can reach and click the floating button.
    const rect = badge.current?.getBoundingClientRect();
    if (rect && event.clientX >= rect.left - 6 && event.clientX <= rect.right + 6 && event.clientY >= rect.top - 6 && event.clientY <= rect.bottom + 6) return;
    anchor.current = { left: event.clientX + 14, top: event.clientY + 14 };
    if (moveFrame.current === null) moveFrame.current = requestAnimationFrame(() => { moveFrame.current = null; placeBadge(); });
  };

  const copy = async (entries, all = false) => {
    if (copying.current || !entries.length) return;
    const version = requestVersion.current;
    copying.current = true; setBusy(true); setFeedback(null);
    clearTimeout(feedbackTimer.current);
    try {
      await navigator.clipboard.writeText(entries.map((entry) => entry.id).join("\n"));
      if (version !== requestVersion.current) return;
      if (!all) setIds((current) => current.some((entry) => entry.id === entries[0].id) ? current : [...current, { id: entries[0].id, kind: entries[0].kind }]);
      setFeedback({ id: all ? "all" : entries[0].id, message: all ? `已複製全部 ${entries.length} 個 ID` : "已複製，已加入清單" });
      feedbackTimer.current = setTimeout(() => setFeedback(null), 2200);
    } catch {
      if (version === requestVersion.current) setFeedback({ error: true, message: "無法複製，請確認剪貼簿權限後再試。" });
    } finally {
      if (version === requestVersion.current) { copying.current = false; setBusy(false); }
    }
  };

  return <Tools ref={host} onPointerOver={enabled ? (event) => { if (event.pointerType !== "touch") reveal(event); } : undefined} onPointerMove={enabled ? follow : undefined} onPointerLeave={enabled ? leave : undefined}
    onClickCapture={(event) => {
      if (!enabled) return;
      if (event.target.closest?.("[data-preview-uuid-ui]")) return;
      if (event.target.closest?.(controlSelector)) hide();
      else reveal(event, true);
    }} onFocusCapture={enabled ? (event) => reveal(event, true) : undefined} onDragStartCapture={enabled ? hide : undefined}>
    {enabled && <Dock data-preview-uuid-ui data-record-export-exclude="true" data-html2canvas-ignore="true">
      <Toggle type="button" aria-label={`已收集 ID（${ids.length}）`} aria-expanded={open} onClick={() => { hide(); setOpen((value) => !value); }}>
        <FiLayers aria-hidden="true" /><span>ID 清單</span><Count>{ids.length}</Count>
      </Toggle>
      {open && <ListPanel role="region" aria-label="已收集的區塊 ID">
        <ListHeading><strong>已收集 {ids.length} 個 ID</strong><IconButton type="button" aria-label="關閉 ID 清單" onClick={() => setOpen(false)}><FiX /></IconButton></ListHeading>
        {ids.length ? <>
          <IdList>{ids.map((entry) => <li key={entry.id}><div><small>{entry.kind}</small><code>{entry.id}</code></div><IconButton type="button" aria-label={`移除 ${entry.id}`} disabled={busy} onClick={() => { setIds((current) => current.filter((item) => item.id !== entry.id)); setFeedback(null); }}><FiX /></IconButton></li>)}</IdList>
          <ListActions><button type="button" disabled={busy} onClick={() => { setIds([]); setFeedback(null); }}><FiTrash2 aria-hidden="true" />清空</button><button type="button" disabled={busy} onClick={() => copy(ids, true)}>{feedback?.id === "all" ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}{busy ? "複製中…" : feedback?.id === "all" ? "已複製全部" : "複製全部 ID"}</button></ListActions>
        </> : <Empty>點區塊上的 ID 複製後，會自動加入這裡。<small>依複製順序收集，重複 ID 只保留一次。</small></Empty>}
        {feedback && <Notice $inline role={feedback.error ? "alert" : "status"} $error={feedback.error}>{feedback.message}</Notice>}
      </ListPanel>}
      {!open && feedback && <Notice role={feedback.error ? "alert" : "status"} $error={feedback.error}>{feedback.message}</Notice>}
    </Dock>}
    {children}
    {enabled && active && createPortal(<Badge ref={badge} data-preview-uuid-ui data-record-export-exclude="true" data-html2canvas-ignore="true" onPointerEnter={keep} onPointerLeave={leave}>
      <button type="button" disabled={busy} aria-label={`複製${active.kind} ${active.id}`} title={`${active.id} · 複製並加入清單`}
        onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); copy([active]); }}>
        {feedback?.id === active.id ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}<span>{busy ? "複製中…" : feedback?.id === active.id ? "已複製" : `${active.kind} · ${shortId(active.id)}`}</span>
      </button>
      <IconButton type="button" aria-label="隱藏區塊 ID" onClick={(event) => { event.stopPropagation(); hide(); }}><FiX /></IconButton>
    </Badge>, host.current?.closest('[role="dialog"]') || document.body)}
  </Tools>;
}

const Tools = styled.div`min-width: 0; position: relative;`;
const Dock = styled.div`
  position: sticky; top: 8px; z-index: 46; width: fit-content; max-width: 100%; height: 0; margin: 0 8px 0 auto; /* floats over the preview; takes no layout space */
  font: 12px/1.5 system-ui, sans-serif; color: var(--app-text, #334155);
  @media print { display: none; }
`;
const Toggle = styled.button`
  display: flex; align-items: center; gap: 7px; padding: 7px 10px; min-height: 34px;
  border: 1px solid var(--app-border, #d9e1ec); border-radius: 9px;
  background: var(--app-surface, #fff); color: inherit; cursor: pointer; font: inherit;
  box-shadow: 0 2px 7px #00000008;
  &:focus-visible { outline: 2px solid #5474b5; outline-offset: 2px; }
`;
const Count = styled.span`padding: 0 5px; min-width: 14px; border-radius: 5px; text-align: center; background: var(--app-surface-soft, #edf2fa); font-variant-numeric: tabular-nums;`;
const ListPanel = styled.div`
  position: absolute; top: 40px; right: 0; width: min(344px, calc(100vw - 40px));
  border: 1px solid var(--app-border, #d9e1ec); border-radius: 12px;
  background: var(--app-surface, #fff); box-shadow: 0 8px 32px #0002; padding: 12px; box-sizing: border-box;
`;
const ListHeading = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 8px;`;
const IconButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 30px; height: 30px;
  padding: 5px; border: 0; border-radius: 5px; background: transparent; color: inherit; cursor: pointer;
  &:hover { background: var(--app-surface-soft, #edf2fa); } &:focus-visible { outline: 2px solid #5474b5; }
  &:disabled { opacity: .5; cursor: wait; }
`;
const IdList = styled.ol`
  margin: 8px 0; padding: 0; list-style: none; max-height: min(260px, 40dvh); overflow: auto; overscroll-behavior: contain;
  li { display: flex; align-items: center; gap: 6px; padding: 8px 0; border-bottom: 1px solid var(--app-border, #e4e9f0); }
  li > div { flex: 1; min-width: 0; } small { display: block; color: var(--app-muted-text, #718096); }
  code { display: block; overflow-wrap: anywhere; font-size: 11px; color: var(--preview-uuid-blue, #3877ce); }
`;
const ListActions = styled.div`
  display: flex; justify-content: space-between; gap: 8px; padding-top: 8px;
  button { display: flex; align-items: center; gap: 6px; border: 1px solid var(--app-border, #d9e1ec); border-radius: 7px; background: var(--app-surface, #fff); color: inherit; padding: 7px 9px; font: inherit; cursor: pointer; }
  button:last-child { background: #355e9c; border-color: #355e9c; color: #fff; } button:disabled { opacity: .6; cursor: wait; }
`;
const Empty = styled.p`margin: 12px 0 4px; color: var(--app-muted-text, #68788d); small { display: block; margin-top: 5px; }`;
const Notice = styled.div`position: ${({ $inline }) => $inline ? "static" : "absolute"}; right: 0; top: 40px; width: ${({ $inline }) => $inline ? "auto" : "max-content"}; max-width: min(320px, calc(100vw - 40px)); border-radius: 7px; padding: 6px 9px; background: var(--app-surface, #fff); box-shadow: ${({ $inline }) => $inline ? "none" : "0 2px 12px #0002"}; color: ${({ $error }) => $error ? "#b33e36" : "var(--app-muted-text, #596d88)"};`;
const Badge = styled.div`
  position: fixed; z-index: 1600; display: flex; align-items: center;
  max-width: calc(100vw - 24px); padding: 2px; border: 1px solid var(--app-border, #d2dce9); border-radius: 8px;
  background: var(--app-surface, #fff); color: var(--preview-uuid-blue, #3877ce); box-shadow: 0 2px 9px #0001;
  opacity: .78; transition: opacity .15s; font: 11px/1.4 system-ui, sans-serif;
  &:hover, &:focus-within { opacity: 1; }
  > button:first-child { display: flex; align-items: center; gap: 6px; min-height: 30px; min-width: 0; padding: 5px 7px; border: 0; background: transparent; color: inherit; cursor: pointer; font: inherit; }
  > button:first-child span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  button:focus-visible { outline: 2px solid #5474b5; border-radius: 4px; }
  button:disabled { opacity: .65; cursor: wait; }
  @media (pointer: coarse) { opacity: .95; button { min-height: 40px; } }
  @media (prefers-reduced-motion: reduce) { transition: none; }
  @media print { display: none; }
`;
